import React, { useMemo, useCallback, useEffect } from 'react';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import { getFeatureFlagValue } from '@atlassian/jira-feature-flagging';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { appBundleReadyMark } from '@atlassian/jira-providers-spa-apdex-analytics/src/marks';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import { usePopupController } from '@atlassian/jira-servicedesk-common/src/use-popup-controller';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { useServiceDeskContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/index.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { METRICS_PAGE_ID, NEW_MODE } from '../common/constants';
import type { RestMetric } from '../common/types';
import { transformToState } from '../common/utils/transform/index.tsx';
import { useSLAHierarchicalGoalsEnabled } from '../controllers/enable-sla-hierarchical-goals';
import { useGoalCounter } from '../controllers/goal-counter';
import { useMetricActions } from '../controllers/metric-actions';
import { useFetchPrioritiesService } from '../services/fetch-priorities/index.tsx';
import { slaConfigurationPageLoad } from '../utils/performance-analytics';
import EmptyView from './empty-view';
import { Header } from './header';
import { MetricList } from './metrics';
import { CalendarModalWithTwoTabs } from './reusable-calendars';

type Props = Intl & {
	source: RestMetric;
};

const fetchHierarchicalGoalsEnabledPerServiceDeskIds = () =>
	getFeatureFlagValue('jsm-sla-enable-hierarchical-goals-per-service-desk', {
		enabledServiceDeskIds: [],
	});

export const SlaSettingsAppMainView = ({ intl: { formatMessage }, source }: Props) => {
	const { metrics, canManageFields } = useMemo(
		() => transformToState(source, formatMessage),
		[formatMessage, source],
	);
	const [isCalendarModalOpen, onToggleCalendarModal] = usePopupController();
	const [{ metricMode }, { updatePath }] = useMetricActions();
	const onAdd = useCallback(() => {
		updatePath(null, NEW_MODE);
	}, [updatePath]);
	const { baseUrl } = useCommonPageContext();

	const [{ data: serviceDeskContext }] = useServiceDeskContext();
	const { serviceDeskId } = serviceDeskContext || {};

	const [{ data: pageContextData }] = usePageContext();
	const { projectId } = pageContextData?.projectContext || {};

	const { enabledServiceDeskIds: slaHierarchicalGoalsEnabledServiceDeskIds = [] } =
		fetchHierarchicalGoalsEnabledPerServiceDeskIds();
	const [{ isSLAHierarchicalGoalsEnabled }, { updateSLAHierarchicalGoalsEnabledFF }] =
		useSLAHierarchicalGoalsEnabled();

	// If the enabledServiceDeskIds list has first entry as -1,
	// -> It implies that the SLA Hierarchical goals is enabled for all projects.
	// If the enabledServiceDeskIds list has multiple Ids,
	// -> We need to see if the current projectId is present in the list.
	// [-1] => Enable for all Service desks
	// [2, 3] => Enable for only 2 & 3
	// [-1, 2, 3] => Ignore -1 & enable for 2 & 3
	// [] => no projects are enabled with Hierarchical goals.
	if (
		slaHierarchicalGoalsEnabledServiceDeskIds.length > 0 &&
		((slaHierarchicalGoalsEnabledServiceDeskIds.length === 1 &&
			slaHierarchicalGoalsEnabledServiceDeskIds[0] === -1) ||
			// @ts-expect-error - TS2345 - Argument of type 'any' is not assignable to parameter of type 'never'.ts(2345)
			slaHierarchicalGoalsEnabledServiceDeskIds.includes(serviceDeskId))
	) {
		updateSLAHierarchicalGoalsEnabledFF(true);
	}

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [_, { fetchPriorities }] = useFetchPrioritiesService();
	useEffect(() => {
		fetchPriorities(baseUrl, projectId, formatMessage);
	}, [fetchPriorities, baseUrl, projectId, formatMessage]);

	const [, actions] = useGoalCounter();

	useEffect(() => {
		const calculateTotalNumberOfGoalsInProject = () => {
			let goalCount = 0;
			let subGoalCount = 0;

			Object.keys(metrics).forEach((key) => {
				const { goals } = metrics[key].config;
				goalCount += Object.keys(goals).length;

				Object.values(goals).forEach((goal) => {
					if (goal.subGoals) {
						subGoalCount += goal.subGoals.length;
					}
				});
			});
			actions.setGoalCounter(goalCount - Object.keys(metrics).length);
			return { goalCount, subGoalCount };
		};
		const { goalCount, subGoalCount } = calculateTotalNumberOfGoalsInProject();
		fireTrackAnalytics(createAnalyticsEvent({}), 'slaSettingsPage viewed', {
			slaCount: Object.keys(metrics).length,
			totalGoalCount: goalCount,
			totalSubGoalCount: subGoalCount,
		});
	}, [createAnalyticsEvent, metrics, actions, metricMode]);

	if (!source) {
		return <Spinner />;
	}

	if (Object.keys(metrics).length > 0 || metricMode === NEW_MODE) {
		return (
			<div id="sla-settings-app">
				<Header
					showActions
					onToggleCalendarPopup={onToggleCalendarModal}
					showBanner={isSLAHierarchicalGoalsEnabled}
				/>
				<MetricList metrics={metrics} canManageFields={canManageFields} />
				<CalendarModalWithTwoTabs
					isCalendarModalOpen={isCalendarModalOpen}
					onToggleCalendarModal={onToggleCalendarModal}
				/>
				<JsmPageInteractiveEvent
					metricKey={METRICS_PAGE_ID}
					withMarks={[appBundleReadyMark]}
					// @ts-expect-error - TS2322 - Type 'PageLoadMetric' is not assignable to type 'BM3Metric'.
					metric={slaConfigurationPageLoad}
				/>
			</div>
		);
	}
	return (
		<>
			<Header
				showActions={false}
				onToggleCalendarPopup={onToggleCalendarModal}
				showBanner={isSLAHierarchicalGoalsEnabled}
			/>
			<EmptyView onAdd={onAdd} />
		</>
	);
};

export const SlaSettingsAppMain = injectIntl(SlaSettingsAppMainView);
